import { FC } from 'react';
import { CalendarBlankIcon, FunnelIcon } from '@phosphor-icons/react';
import { ChartErrorState } from '../../../leopart-update/components/AthleteInjuryCharts/charts/_shared/ChartErrorState';
import Typhography from '../../../shared/Typhography/Typhography';
import LoadingPage from '../../../shared/LoadingPage/LoadingPage';
import EmptyContainer from '../../../shared/EmptyContainer/EmptyContainer';
import { useAllenamentiTaxonomy } from '../hooks/useAllenamentiTaxonomy';
import { readErrorMessage } from '../allenamenti.constants';
import { useAllenamentiAnalysis } from './useAllenamentiAnalysis';
import PeriodSwitch from './PeriodSwitch';
import AnalysisKpiRow from './AnalysisKpiRow';
import CategoryBars from './CategoryBars';
import PhysicalSubcategoryBars from './PhysicalSubcategoryBars';
import GameDensityPanel from './GameDensityPanel';
import PlayerGroupPanel from './PlayerGroupPanel';
import MalloIdcPanel from './MalloIdcPanel';
import TrendChart from './TrendChart';
import BlocksTable from './BlocksTable';
import AnalysisFilterBar from './AnalysisFilterBar';
import CategoryReport from './CategoryReport';
import { useAnalysisFilter } from './useAnalysisFilter';
import styles from './AnalysisView.module.scss';

interface IAnalysisViewProps {
  teamId: string;
}

/**
 * Vista Analisi del modulo Allenamenti (M3): composizione del lavoro
 * PIANIFICATO di squadra su settimana/mese/stagione. Vive dentro il tab
 * `allenamenti` già montato in TeamDetail — non una route a sé (le route
 * `/dashboard/teams/:teamId/allenamenti`-simili senza link sono la trappola
 * in cui sono già cadute TrainingStatisticsDashboard e WeeklyReports, vedi
 * design M2/M3 §0 e R1).
 *
 * Shell di sola composizione: tutta la logica (periodo, fetch, join
 * tassonomia) vive in useAllenamentiAnalysis/useAllenamentiTaxonomy.
 */
const AnalysisView: FC<IAnalysisViewProps> = ({ teamId }) => {
  const { categories, bySlug, isLoading: isTaxonomyLoading } = useAllenamentiTaxonomy();
  const {
    period,
    changePeriod,
    label,
    goPrev,
    goNext,
    data,
    isLoading,
    isError,
    error,
    refetch,
    blocks,
    isBlocksLoading,
  } = useAllenamentiAnalysis(teamId);

  const filter = useAnalysisFilter(
    blocks,
    categories,
    bySlug,
    data?.kpi.totalMinutes ?? 0,
  );

  const isBusy = isLoading || isTaxonomyLoading;
  const hasBlocks = !!data && data.kpi.totalBlocks > 0;

  return (
    <div className={styles.view}>
      <Typhography size="regular-12" className={styles.disclaimer}>
        Minuti pianificati di squadra — non confrontabili con lo sforzo percepito dei singoli né
        con i dati GPS.
      </Typhography>

      <PeriodSwitch period={period} label={label} onChange={changePeriod} onPrev={goPrev} onNext={goNext} />

      {isError && <ChartErrorState onRetry={refetch} message={readErrorMessage(error)} />}

      {!isError && isBusy && <LoadingPage />}

      {!isError && !isBusy && data && !hasBlocks && (
        <EmptyContainer
          Icon={CalendarBlankIcon}
          title="Nessun blocco pianificato in questo periodo"
          description={`${label} — aggiungili dalla vista Settimana.`}
          addButton={false}
          onClick={() => {}}
        />
      )}

      {!isError && !isBusy && data && hasBlocks && (
        <>
          <AnalysisKpiRow kpi={data.kpi} compare={data.compare} bySlug={bySlug} />

          <div className={styles.chartsRow}>
            <CategoryBars slices={data.byCategory} bySlug={bySlug} />
            <PhysicalSubcategoryBars slices={data.bySubcategory} bySlug={bySlug} />
          </div>

          {/* Pannelli contesto di gioco + Mallo (ADR 029): full-width, dopo
              la composizione e prima del trend, come nel prototipo. */}
          <GameDensityPanel density={data.gameDensity ?? null} />
          <PlayerGroupPanel data={data.playerDensity ?? null} />
          <MalloIdcPanel mallo={data.malloIdc ?? null} />

          <TrendChart series={data.series} granularity={data.period.granularity} bySlug={bySlug} />

          {/* Confine di scope: i pannelli sopra sono la panoramica del periodo
              intero; da qui in giù il filtro restringe blocchi e report. Detto
              a parole, altrimenti KPI/grafici invariati al filtro sembrano un bug. */}
          <div className={styles.sectionHead}>
            <Typhography type="h3" size="bold-14" className={styles.sectionTitle}>
              Esplora i blocchi del periodo
            </Typhography>
            <Typhography size="regular-12" className={styles.sectionHint}>
              Il filtro agisce solo su questa sezione — la panoramica sopra resta sul periodo intero.
            </Typhography>
          </div>

          <AnalysisFilterBar
            categoryOptions={filter.categoryOptions}
            subcategoryOptions={filter.subcategoryOptions}
            categoryIds={filter.categoryIds}
            subcategoryIds={filter.subcategoryIds}
            onCategories={filter.setCategories}
            onSubcategories={filter.setSubcategoryIds}
            onClear={filter.clear}
            isFiltering={filter.isFiltering}
          />

          {filter.isFiltering && filter.filteredBlocks.length === 0 ? (
            <EmptyContainer
              Icon={FunnelIcon}
              title="Nessun blocco per questa selezione"
              description="In questo periodo non ci sono blocchi con le categorie scelte. Cambia selezione o azzera il filtro."
              addButton={false}
              onClick={() => {}}
            />
          ) : (
            <>
              {filter.isFiltering && (
                <CategoryReport rows={filter.reportRows} chartSlices={filter.chartSlices} />
              )}
              <BlocksTable blocks={filter.filteredBlocks} isLoading={isBlocksLoading} bySlug={bySlug} />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default AnalysisView;
